import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { resolveMedia } from "@/utils/mediaResolver";
import {
  ShoppingCart, Plus, Minus, CreditCard, Banknote, ArrowLeft,
  Monitor, Coffee, Loader2, CheckCircle2
} from "lucide-react";

export default function AutoatendimentoView({ authAxios, onBack, restaurantName }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("menu"); // menu | cart | payment | success
  const [processing, setProcessing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const inactivityTimer = useRef(null);
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const resetKiosk = useCallback(() => {
    setCart([]);
    setPhase("menu");
    setActiveCat("all");
    setOrderResult(null);
    setCustomerName("");
    setCustomerPhone("");
  }, []);

  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        if (phase !== "menu" || cart.length > 0) resetKiosk();
      }, 60000);
    };
    window.addEventListener("touchstart", resetTimer);
    window.addEventListener("click", resetTimer);
    resetTimer();
    return () => {
      clearTimeout(inactivityTimer.current);
      window.removeEventListener("touchstart", resetTimer);
      window.removeEventListener("click", resetTimer);
    };
  }, [phase, cart.length, resetKiosk]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/pos/menu")
      .then(r => { setCategories(r.data.categories || []); setProducts(r.data.all_products || []); })
      .catch(() => toast.error("Erro ao carregar menu"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => activeCat === "all" || p.category_id === activeCat);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) return prev.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { product_id: product.id, product_name: product.name, unit_price: product.price, quantity: 1 }];
    });
  };

  const updateQty = (productId, delta) => {
    setCart(prev => prev.map(i => {
      if (i.product_id !== productId) return i;
      return { ...i, quantity: i.quantity + delta };
    }).filter(i => i.quantity > 0));
  };

  const total = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);

  const handleCheckout = async (paymentMethod) => {
    setProcessing(true);
    try {
      const res = await authAxios.post("/pos/quick-order", {
        items: cart,
        payment_method: paymentMethod,
        customer_name: customerName || "Balcão",
        customer_phone: customerPhone || undefined,
        notes: "Balcão",
      });
      setOrderResult(res.data);
      setPhase("success");
    } catch { toast.error("Erro ao processar pedido"); }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div className="min-h-screen bg-[#111] flex flex-col items-center justify-center p-8 text-center" data-testid="kiosk-success">
        <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 animate-in zoom-in-50">
          <CheckCircle2 className="w-14 h-14 text-emerald-400" />
        </div>
        <h1 className="text-3xl font-black text-white mb-2">Pedido Confirmado!</h1>
        <p className="text-emerald-400 text-lg font-bold mb-2">#{orderResult?.id?.slice(-6).toUpperCase()}</p>
        <p className="text-5xl font-black text-white my-6">{orderResult?.total?.toFixed(2)} EUR</p>
        <p className="text-gray-400 text-sm mb-10">Seu pedido sera preparado em instantes</p>
        <Button className="h-14 px-12 bg-emerald-600 hover:bg-emerald-700 text-lg font-bold rounded-2xl" onClick={resetKiosk} data-testid="kiosk-new-order">
          NOVO PEDIDO
        </Button>
        <button onClick={onBack} className="mt-6 text-gray-500 text-xs underline" data-testid="kiosk-exit-btn">Sair do balcão</button>
      </div>
    );
  }

  if (phase === "payment") {
    return (
      <div className="min-h-screen bg-[#111] flex flex-col" data-testid="kiosk-payment">
        <div className="bg-[#1a1a1a] px-6 py-4 flex items-center gap-4 border-b border-gray-800">
          <button onClick={() => setPhase("cart")} className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center hover:bg-gray-700" data-testid="kiosk-payment-back">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Forma de Pagamento</h1>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
          <p className="text-gray-400 text-sm">Total a pagar</p>
          <p className="text-6xl font-black text-white">{total.toFixed(2)} EUR</p>
          <div className="w-full max-w-sm mt-6 space-y-3">
            <Input
              placeholder="Nome (opcional)"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              className="h-14 bg-gray-800 border-gray-700 text-white text-center text-lg rounded-2xl"
              data-testid="kiosk-customer-name"
            />
            <Input
              placeholder="Telefone (opcional)"
              value={customerPhone}
              onChange={e => setCustomerPhone(e.target.value)}
              className="h-14 bg-gray-800 border-gray-700 text-white text-center text-lg rounded-2xl"
              type="tel"
              data-testid="kiosk-customer-phone"
            />
          </div>
          <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
            {[
              { method: "card", label: "CARTAO", icon: <CreditCard className="w-8 h-8" />, bg: "bg-blue-600 hover:bg-blue-700" },
              { method: "cash", label: "DINHEIRO", icon: <Banknote className="w-8 h-8" />, bg: "bg-emerald-600 hover:bg-emerald-700" },
              { method: "bizum", label: "BIZUM", icon: <span className="text-xl font-black">Biz</span>, bg: "bg-[#05C3DD] hover:bg-[#04a8bf]" },
              { method: "mbway", label: "MBWAY", icon: <span className="text-xl font-black">MB</span>, bg: "bg-[#E42313] hover:bg-[#c01f10]" },
            ].map(pm => (
              <Button
                key={pm.method}
                className={`h-24 ${pm.bg} font-bold text-lg flex-col gap-2 rounded-2xl`}
                onClick={() => handleCheckout(pm.method)}
                disabled={processing}
                data-testid={`kiosk-pay-${pm.method}`}
              >
                {processing ? <Loader2 className="w-6 h-6 animate-spin" /> : <>{pm.icon}<span className="text-xs">{pm.label}</span></>}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "cart") {
    return (
      <div className="min-h-screen bg-[#111] flex flex-col" data-testid="kiosk-cart">
        <div className="bg-[#1a1a1a] px-6 py-4 flex items-center gap-4 border-b border-gray-800">
          <button onClick={() => setPhase("menu")} className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center hover:bg-gray-700" data-testid="kiosk-cart-back">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Seu Pedido</h1>
          <Badge className="bg-emerald-500/20 text-emerald-400 border-0 ml-auto text-sm">{totalItems} itens</Badge>
        </div>
        <ScrollArea className="flex-1 p-4">
          <div className="max-w-lg mx-auto space-y-3">
            {cart.map(item => (
              <div key={item.product_id} className="bg-[#1a1a1a] rounded-2xl p-4 flex items-center gap-4 border border-gray-800" data-testid={`kiosk-cart-item-${item.product_id?.slice(-6)}`}>
                <div className="flex-1">
                  <p className="text-white font-semibold">{item.product_name}</p>
                  <p className="text-gray-500 text-sm">{item.unit_price.toFixed(2)} EUR c/u</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => updateQty(item.product_id, -1)} className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-white hover:bg-red-900/50 active:scale-95">
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-white font-bold text-xl w-8 text-center">{item.quantity}</span>
                  <button onClick={() => updateQty(item.product_id, 1)} className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-white hover:bg-emerald-900/50 active:scale-95">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-emerald-400 font-bold text-lg w-20 text-right">{(item.quantity * item.unit_price).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="border-t border-gray-800 bg-[#1a1a1a] p-6">
          <div className="flex justify-between items-baseline max-w-lg mx-auto mb-4">
            <span className="text-gray-400 font-medium">TOTAL</span>
            <span className="text-4xl font-black text-white">{total.toFixed(2)} EUR</span>
          </div>
          <Button className="w-full max-w-lg mx-auto block h-16 bg-emerald-600 hover:bg-emerald-700 text-xl font-bold rounded-2xl" onClick={() => setPhase("payment")} data-testid="kiosk-go-to-payment">
            PAGAR
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#111] overflow-hidden" data-testid="kiosk-menu">
      <div className="bg-[#1a1a1a] px-6 py-4 flex items-center gap-4 border-b border-gray-800 flex-shrink-0">
        <button onClick={onBack} className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center hover:bg-gray-700" data-testid="kiosk-exit">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <Monitor className="w-5 h-5 text-emerald-400" />
        <h1 className="text-xl font-bold text-white flex-1">{restaurantName || "Faca seu Pedido"}</h1>
        {cart.length > 0 && (
          <button onClick={() => setPhase("cart")} className="relative flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95" data-testid="kiosk-view-cart">
            <ShoppingCart className="w-5 h-5" />
            <span>{totalItems}</span>
            <span className="hidden sm:inline">- {total.toFixed(2)} EUR</span>
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] px-4 py-3 flex gap-2 overflow-x-auto border-b border-gray-800 flex-shrink-0" data-testid="kiosk-categories">
        <button
          className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeCat === "all" ? "bg-emerald-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
          onClick={() => setActiveCat("all")}
        >Todos</button>
        {categories.map(cat => (
          <button key={cat.id}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeCat === cat.id ? "bg-emerald-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
            onClick={() => setActiveCat(cat.id)}
          >{cat.name}</button>
        ))}
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map(product => {
            const inCart = cart.find(i => i.product_id === product.id);
            return (
              <button key={product.id} onClick={() => addToCart(product)}
                className={`relative bg-[#1a1a1a] rounded-2xl border-2 p-4 text-left transition-all hover:shadow-lg active:scale-[0.97] ${inCart ? "border-emerald-500 shadow-emerald-500/10 shadow-lg" : "border-gray-800 hover:border-gray-600"}`}
                data-testid={`kiosk-product-${product.id?.slice(-6)}`}
              >
                {inCart && (
                  <div className="absolute -top-2 -right-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg z-10">
                    {inCart.quantity}
                  </div>
                )}
                {(product.pos_media_url || product.image_url) ? (
                  product.pos_media_type === "video" && product.pos_media_url ? (
                    <video src={resolveMedia(product.pos_media_url)} className="w-full h-24 object-cover rounded-xl mb-3" muted loop playsInline autoPlay />
                  ) : (
                    <img src={resolveMedia(product.pos_media_url || product.image_url)} alt={product.name} className="w-full h-24 object-cover rounded-xl mb-3" />
                  )
                ) : (
                  <div className="w-full h-24 bg-gray-800 rounded-xl mb-3 flex items-center justify-center">
                    <Coffee className="w-8 h-8 text-gray-600" />
                  </div>
                )}
                <p className="text-sm font-semibold text-white truncate">{product.name}</p>
                <p className="text-emerald-400 font-bold mt-1">{product.price?.toFixed(2)} EUR</p>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-20 text-gray-600">
              <Coffee className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum produto disponivel</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {cart.length > 0 && (
        <div className="border-t border-gray-800 bg-[#1a1a1a] p-4 flex-shrink-0">
          <Button className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-lg font-bold rounded-2xl flex items-center justify-center gap-3" onClick={() => setPhase("cart")} data-testid="kiosk-checkout-bar">
            <ShoppingCart className="w-5 h-5" />
            Ver Pedido ({totalItems}) - {total.toFixed(2)} EUR
          </Button>
        </div>
      )}
    </div>
  );
}
